import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
// @ts-ignore
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import {
  documentService,
  DocumentType,
  JobDocument,
  UploadConstraints,
  FormPdfFreshness,
} from '../../services/documentService';
import { debugLog } from '../../utils/debugLog';
import DocumentGallery from './DocumentGallery';
import PDFViewerIframe from './PDFViewerIframe';

// Prevents parent re-renders from propagating into the lightbox and causing
// iframe repaints / PDF flashes.
const MemoizedLightbox = React.memo(Lightbox, (prevProps: any, nextProps: any) => {
  return (
    prevProps.open === nextProps.open &&
    prevProps.index === nextProps.index &&
    prevProps.slides === nextProps.slides &&
    prevProps.close === nextProps.close &&
    prevProps.carousel === nextProps.carousel &&
    prevProps.animation === nextProps.animation &&
    prevProps.render === nextProps.render &&
    prevProps.on === nextProps.on &&
    prevProps.controller === nextProps.controller &&
    prevProps.styles === nextProps.styles
  );
});

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
const VIEWABLE_EXTENSIONS = [...IMAGE_EXTENSIONS, 'pdf'];

const extOf = (doc: JobDocument): string => doc.fileExtension.toLowerCase().replace('.', '');
const isImage = (doc: JobDocument): boolean => IMAGE_EXTENSIONS.includes(extOf(doc));
const isViewable = (doc: JobDocument): boolean => VIEWABLE_EXTENSIONS.includes(extOf(doc));

const blobToDataUrl = (blob: Blob): Promise<string | null> =>
  new Promise<string | null>(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });

interface JobDocumentsTabProps {
  jobId: string;
  uploadedDocuments: JobDocument[];
  loadingDocuments: boolean;
  isOffline: boolean;
  uploadConstraints: UploadConstraints | null;
  onReloadDocuments: (forceRefresh?: boolean) => void | Promise<void>;
  onRemoveDocument: (documentId: string) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  staleForms?: FormPdfFreshness[];
  regeneratingFormIds?: Set<string>;
  checkingFreshness?: boolean;
}

const JobDocumentsTab: React.FC<JobDocumentsTabProps> = ({
  jobId,
  uploadedDocuments,
  loadingDocuments,
  isOffline,
  uploadConstraints,
  onReloadDocuments,
  onRemoveDocument,
  onError,
  onSuccess,
  staleForms,
  regeneratingFormIds,
  checkingFreshness,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxSlides, setLightboxSlides] = useState<any[]>([]);
  const [lightboxSection, setLightboxSection] = useState<'uploaded' | 'other'>('uploaded');
  const [, setLightboxLoading] = useState(false);

  const [sharedThumbnailUrls, setSharedThumbnailUrls] = useState<{ [key: string]: string | null }>({});
  const [sharedFullImageUrls, setSharedFullImageUrls] = useState<{ [key: string]: string | null }>({});

  const loadingImagesRef = useRef<Set<string>>(new Set());

  // Patch lightbox slide src values when full-resolution images arrive.
  // Only updates when something actually changed, and preserves PDF (custom)
  // slide references so PDFViewerIframe doesn't remount.
  useEffect(() => {
    if (!lightboxOpen || lightboxSlides.length === 0) return;

    let hasChanges = false;
    for (const slide of lightboxSlides) {
      if (slide.type === 'custom') continue;
      const doc = slide.document;
      if (!doc) continue;
      const newSrc = sharedFullImageUrls[doc.id] || sharedThumbnailUrls[doc.id] || '';
      if (newSrc !== slide.src) {
        hasChanges = true;
        break;
      }
    }
    if (!hasChanges) return;

    debugLog('[Lightbox:SlideUpdate] Changes detected, updating slides');
    setLightboxSlides(prev =>
      prev.map(slide => {
        if (slide.type === 'custom') return slide;
        const doc = slide.document;
        if (!doc) return slide;
        const newSrc = sharedFullImageUrls[doc.id] || sharedThumbnailUrls[doc.id] || '';
        return newSrc !== slide.src ? { ...slide, src: newSrc } : slide;
      })
    );
  }, [sharedFullImageUrls, sharedThumbnailUrls, lightboxOpen, lightboxSlides]);

  const handleDocumentClick = useCallback(async (document: JobDocument) => {
    try {
      await documentService.openDocument(document);
    } catch (err) {
      console.error('Failed to open document:', err);
      onError('Failed to open document');
    }
  }, [onError]);

  const handleDownloadDocument = useCallback(async (document: JobDocument, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await documentService.saveDocument(document);
    } catch (err) {
      console.error('Failed to download document:', err);
      onError('Failed to download document');
    }
  }, [onError]);

  const handleDeleteDocument = useCallback(async (document: JobDocument, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${document.originalFileName}"?`)) return;
    try {
      await documentService.deleteDocument(document.id);
      onSuccess('Document deleted successfully');
      onRemoveDocument(document.id);
    } catch (err) {
      console.error('Failed to delete document:', err);
      onError('Failed to delete document');
    }
  }, [onError, onRemoveDocument, onSuccess]);

  const handleThumbnailLoaded = useCallback((documentId: string, url: string | null) => {
    setSharedThumbnailUrls(prev => (prev[documentId] === url ? prev : { ...prev, [documentId]: url }));
  }, []);

  const prepareGallerySlides = useCallback((documents: JobDocument[]) => {
    return documents
      .filter(isViewable)
      .map(doc => {
        if (extOf(doc) === 'pdf') {
          return { src: '', alt: doc.originalFileName, type: 'custom', document: doc };
        }
        const src = sharedFullImageUrls[doc.id] || sharedThumbnailUrls[doc.id] || '';
        return { src, alt: doc.originalFileName, type: 'image', document: doc };
      });
  }, [sharedFullImageUrls, sharedThumbnailUrls]);

  const loadFullImageDataUrl = useCallback(async (documentId: string): Promise<string | null> => {
    if (sharedFullImageUrls[documentId]) return sharedFullImageUrls[documentId];
    if (loadingImagesRef.current.has(documentId)) return null;

    loadingImagesRef.current.add(documentId);
    try {
      const blob = await documentService.downloadDocument(documentId);
      const dataUrl = await blobToDataUrl(blob);
      setSharedFullImageUrls(prev => ({ ...prev, [documentId]: dataUrl }));
      return dataUrl;
    } catch (error) {
      console.warn('[Lightbox:FullImage] Failed to load full image for:', documentId, error);
      return null;
    } finally {
      loadingImagesRef.current.delete(documentId);
    }
  }, [sharedFullImageUrls]);

  const loadThumbnailOrFullImage = useCallback(async (document: JobDocument) => {
    try {
      if (document.hasThumbnail) {
        const url = await documentService.getThumbnailDataUrl(document.id);
        setSharedThumbnailUrls(prev => ({ ...prev, [document.id]: url }));
      } else {
        const blob = await documentService.downloadDocument(document.id);
        const dataUrl = await blobToDataUrl(blob);
        setSharedThumbnailUrls(prev => ({ ...prev, [document.id]: dataUrl }));
      }
    } catch (error) {
      console.warn('Failed to load preview for image:', document.id, error);
      setSharedThumbnailUrls(prev => ({ ...prev, [document.id]: null }));
    }
  }, []);

  const handleDocumentGalleryClick = useCallback(async (document: JobDocument, section: 'uploaded' | 'other') => {
    const sectionDocuments = uploadedDocuments.filter(d => {
      if (!isViewable(d)) return false;
      const isUploaded = d.documentType === DocumentType.Uploaded;
      return section === 'uploaded' ? isUploaded : !isUploaded;
    });

    const clickedIndex = sectionDocuments.findIndex(d => d.id === document.id);
    if (clickedIndex < 0) {
      handleDocumentClick(document);
      return;
    }

    setLightboxLoading(true);
    setLightboxSection(section);

    try {
      const clicked = sectionDocuments[clickedIndex];
      if (isImage(clicked) && !sharedThumbnailUrls[clicked.id]) {
        await loadThumbnailOrFullImage(clicked);
      }

      setLightboxSlides(prepareGallerySlides(sectionDocuments));
      setLightboxIndex(clickedIndex);
      setLightboxOpen(true);

      if (
        isImage(clicked) &&
        clicked.hasThumbnail &&
        !sharedFullImageUrls[clicked.id] &&
        !loadingImagesRef.current.has(clicked.id)
      ) {
        loadFullImageDataUrl(clicked.id);
      }
    } catch (error) {
      console.error('Failed to load lightbox for clicked image:', error);
      setLightboxSlides(prepareGallerySlides(sectionDocuments));
      setLightboxIndex(clickedIndex);
      setLightboxOpen(true);
    } finally {
      setLightboxLoading(false);
    }
  }, [
    uploadedDocuments,
    sharedThumbnailUrls,
    sharedFullImageUrls,
    handleDocumentClick,
    loadThumbnailOrFullImage,
    prepareGallerySlides,
    loadFullImageDataUrl,
  ]);

  const lightboxClose = useCallback(() => {
    setLightboxOpen(false);
    setLightboxLoading(false);
  }, []);
  const lightboxCarousel = useMemo(() => ({ finite: false }), []);
  const lightboxAnimation = useMemo(() => ({ fade: 0, swipe: 0 }), []);
  const lightboxController = useMemo(
    () => ({ closeOnPullDown: true, closeOnBackdropClick: true }),
    []
  );
  const lightboxStyles = useMemo(
    () =>
      ({
        root: { opacity: 1, transition: 'none' },
        container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
        slide: {
          width: '90vw',
          height: '100vh',
          contain: 'layout paint',
          willChange: 'transform',
        },
      } as any),
    []
  );

  const lightboxRender = useMemo(
    () => ({
      slide: ({ slide }: any) => {
        if (slide.type === 'custom' && slide.document) {
          return (
            <Box sx={{ width: '100%', height: '100%' }}>
              <PDFViewerIframe key={slide.document.id} document={slide.document} />
            </Box>
          );
        }
        if (slide.type === 'image' && (!slide.src || slide.src === '')) {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                backgroundColor: 'grey.100',
                p: 4,
              }}
            >
              <ImageIcon sx={{ fontSize: 80, color: 'grey.500', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Failed to load image
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {slide.alt}
              </Typography>
            </Box>
          );
        }
        return undefined;
      },
    }),
    []
  );

  // Refs for values read inside the stable `on.view` handler, so the handler
  // reference never changes and the Lightbox doesn't needlessly re-render.
  const uploadedDocumentsRef = useRef(uploadedDocuments);
  uploadedDocumentsRef.current = uploadedDocuments;
  const lightboxSectionRef = useRef(lightboxSection);
  lightboxSectionRef.current = lightboxSection;
  const sharedThumbnailUrlsRef = useRef(sharedThumbnailUrls);
  sharedThumbnailUrlsRef.current = sharedThumbnailUrls;
  const sharedFullImageUrlsRef = useRef(sharedFullImageUrls);
  sharedFullImageUrlsRef.current = sharedFullImageUrls;
  const loadFullImageDataUrlRef = useRef(loadFullImageDataUrl);
  loadFullImageDataUrlRef.current = loadFullImageDataUrl;

  const lightboxOnHandlers = useMemo(
    () => ({
      view: ({ index }: any) => {
        setLightboxIndex(prev => (prev === index ? prev : index));

        const sectionDocuments = uploadedDocumentsRef.current.filter(d => {
          if (!isViewable(d)) return false;
          const isUploaded = d.documentType === DocumentType.Uploaded;
          return lightboxSectionRef.current === 'uploaded' ? isUploaded : !isUploaded;
        });

        if (index < 0 || index >= sectionDocuments.length) return;
        const current = sectionDocuments[index];
        if (!isImage(current)) return;

        if (!sharedThumbnailUrlsRef.current[current.id]) {
          if (current.hasThumbnail) {
            documentService
              .getThumbnailDataUrl(current.id)
              .then(url => setSharedThumbnailUrls(prev => ({ ...prev, [current.id]: url })))
              .catch(error => {
                console.warn('Failed to load thumbnail for navigated image:', current.id, error);
                setSharedThumbnailUrls(prev => ({ ...prev, [current.id]: null }));
              });
          } else {
            documentService
              .downloadDocument(current.id)
              .then(blobToDataUrl)
              .then(dataUrl => setSharedThumbnailUrls(prev => ({ ...prev, [current.id]: dataUrl })))
              .catch(error => {
                console.warn('Failed to load full image for navigated image:', current.id, error);
                setSharedThumbnailUrls(prev => ({ ...prev, [current.id]: null }));
              });
          }
        }

        if (
          current.hasThumbnail &&
          !sharedFullImageUrlsRef.current[current.id] &&
          !loadingImagesRef.current.has(current.id)
        ) {
          loadFullImageDataUrlRef.current(current.id);
        }
      },
    }),
    []
  );

  return (
    <>
      <DocumentGallery
        jobId={jobId}
        uploadedDocuments={uploadedDocuments}
        loadingDocuments={loadingDocuments}
        isOffline={isOffline}
        uploadConstraints={uploadConstraints}
        onDocumentClick={handleDocumentClick}
        onDocumentGalleryClick={handleDocumentGalleryClick}
        onDownloadDocument={handleDownloadDocument}
        onDeleteDocument={handleDeleteDocument}
        onReloadDocuments={onReloadDocuments}
        thumbnailUrls={sharedThumbnailUrls}
        onThumbnailLoaded={handleThumbnailLoaded}
        staleForms={staleForms}
        regeneratingFormIds={regeneratingFormIds}
        checkingFreshness={checkingFreshness}
      />
      <MemoizedLightbox
        open={lightboxOpen}
        close={lightboxClose}
        index={lightboxIndex}
        carousel={lightboxCarousel}
        animation={lightboxAnimation}
        slides={lightboxSlides}
        render={lightboxRender}
        on={lightboxOnHandlers}
        controller={lightboxController}
        styles={lightboxStyles}
      />
    </>
  );
};

export default JobDocumentsTab;
